import clsx from 'clsx'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Btn from '../Btn'
import { PlainCheckbox, PlainSelect } from '../PlainField'

import http from '../../shared/http'
import { TApi, TRoles } from '../../shared/const'
import { formatVienna } from '../../shared/datetime'
import { notifyError } from '../../shared/errorMessage'
import {
	REMINDER_FREQUENCIES,
	REMINDER_HOURS,
	REMINDER_KINDS,
	REMINDER_WEEKDAYS,
	hourLabel,
	isReminderFormChanged,
	toForm,
	toPayload
} from '../../shared/reminderSettings'

import styles from './style.module.scss'

/**
 * Erinnerungen per E-Mail eines Verwaltungskontos.
 *
 * Eine Komponente für zwei Orte: „Mein Profil" (jede Verwaltungsrolle für sich)
 * und das Fenster eines Kontos unter „Administration" (die Bundesadministration
 * für alle). Wer was darf, entscheidet der Server — ein Landesadmin bekommt für
 * ein fremdes Konto 403, diese Komponente zeigt dann den Fehler statt Feldern.
 *
 * Zwei Arten mit eigenem Takt (aus · täglich · wöchentlich, volle Stunde,
 * Wiener Zeit). Wann eine Mail fällig ist, rechnet nur der Server
 * (`services/adminReminders/schedule.js`); hier steht nur, was er als nächsten
 * Zeitpunkt nennt.
 *
 * Eigener Speichern-Knopf und eigener Zustand: Im Fenster der Administration
 * steht daneben ein anderes Formular (Person, Rolle) mit eigenem Knopf — beides
 * zusammen zu speichern hieße, für eine Erinnerung das Recht zum Bearbeiten
 * des Zugangs zu verlangen.
 */
const KIND_TEXT = {
	[REMINDER_KINDS.ASSIGNED_TICKETS]: {
		title: 'Assigned requirements',
		hint: 'reminderAssignedHint'
	},
	[REMINDER_KINDS.OPEN_REVIEWS]: {
		title: 'Open reviews',
		hint: 'reminderReviewsHint'
	}
}

export default function ReminderSettings({ userId, role, onDirtyChange, onSaved, className }) {
	const { t } = useTranslation()
	const [saved, setSaved] = useState(null)
	const [form, setForm] = useState(null)
	const [targetRole, setTargetRole] = useState(role || null)
	const [loadError, setLoadError] = useState(false)
	const [saving, setSaving] = useState(false)

	useEffect(() => {
		if (!userId) return undefined
		let aktuell = true

		setSaved(null)
		setForm(null)
		setLoadError(false)

		http.get(TApi.ADMIN_REMINDER_SETTINGS(userId), { disableToast: true })
			.then(({ data }) => {
				if (!aktuell) return
				setSaved(data.settings)
				setForm(toForm(data.settings))
				if (data.role) setTargetRole(data.role)
			})
			.catch(() => {
				if (aktuell) setLoadError(true)
			})

		return () => {
			aktuell = false
		}
	}, [userId])

	const dirty = useMemo(() => Boolean(saved && form && isReminderFormChanged(toForm(saved), form)), [saved, form])

	useEffect(() => {
		if (onDirtyChange) onDirtyChange(dirty)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dirty])

	// Beim Aushängen (Fenster zu) gilt nichts mehr als offen.
	useEffect(
		() => () => {
			if (onDirtyChange) onDirtyChange(false)
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	)

	const setField = (kind, field, value) =>
		setForm(current => ({ ...current, [kind]: { ...current[kind], [field]: value } }))

	const handleSave = () => {
		setSaving(true)
		http.put(TApi.ADMIN_REMINDER_SETTINGS(userId), toPayload(form), { disableToast: true })
			.then(({ data }) => {
				setSaved(data.settings)
				setForm(toForm(data.settings))
				toast.success(t('Reminders saved'))
				if (onSaved) onSaved(data.settings)
			})
			.catch(error => notifyError(error, 'Reminders could not be saved.'))
			.finally(() => setSaving(false))
	}

	if (loadError) return <p className={clsx(styles.message, className)}>{t('Reminders could not be loaded.')}</p>
	if (!form) return <p className={clsx(styles.message, className)}>{t('Loading reminders')}</p>

	const frequencyOptions = REMINDER_FREQUENCIES.map(value => ({ value, label: t(`reminderFrequency${value}`) }))
	const weekdayOptions = REMINDER_WEEKDAYS.map(value => ({ value, label: t(`reminderWeekday${value}`) }))
	const hourOptions = REMINDER_HOURS.map(value => ({ value, label: hourLabel(value) }))

	const status = kind => {
		if (dirty) return t('Not saved yet')
		const next = saved?.[kind]?.nextRunAt
		const last = saved?.[kind]?.lastSentAt
		const teile = []
		if (next) teile.push(`${t('Next check')}: ${formatVienna(next, 'dd.MM.yyyy, HH:mm')}`)
		teile.push(last ? `${t('Last sent')}: ${formatVienna(last, 'dd.MM.yyyy, HH:mm')}` : t('No reminder sent yet'))
		return teile.join(' · ')
	}

	return (
		<div className={clsx(styles.root, className)}>
			{Object.values(REMINDER_KINDS).map(kind => {
				const value = form[kind]
				const aus = value.frequency === 'OFF'
				const hint =
					kind === REMINDER_KINDS.OPEN_REVIEWS
						? targetRole === TRoles.FEDERAL_ADMIN
							? 'reminderReviewsHintFederal'
							: 'reminderReviewsHintState'
						: KIND_TEXT[kind].hint

				return (
					<section key={kind} className={styles.kind} data-testid={`reminder-${kind}`}>
						<h3 className={styles.title}>{t(KIND_TEXT[kind].title)}</h3>
						<p className={styles.hint}>{t(hint)}</p>

						<div className={styles.fields}>
							<PlainSelect
								className={styles.field}
								label={t('Rhythm')}
								value={value.frequency}
								options={frequencyOptions}
								onChange={event => setField(kind, 'frequency', event.target.value)}
								aria-label={`${t(KIND_TEXT[kind].title)}: ${t('Rhythm')}`}
							/>
							{value.frequency === 'WEEKLY' && (
								<PlainSelect
									className={styles.field}
									label={t('Weekday')}
									value={value.weekday}
									options={weekdayOptions}
									onChange={event => setField(kind, 'weekday', Number(event.target.value))}
									aria-label={`${t(KIND_TEXT[kind].title)}: ${t('Weekday')}`}
								/>
							)}
							{!aus && (
								<PlainSelect
									className={styles.field}
									label={t('Time (Vienna)')}
									value={value.hour}
									options={hourOptions}
									onChange={event => setField(kind, 'hour', Number(event.target.value))}
									aria-label={`${t(KIND_TEXT[kind].title)}: ${t('Time (Vienna)')}`}
								/>
							)}
						</div>

						{kind === REMINDER_KINDS.ASSIGNED_TICKETS && (
							<PlainCheckbox
								className={styles.instant}
								label={t('Additionally right away when newly assigned')}
								checked={value.instantOnAssign}
								disabled={aus}
								onChange={event => setField(kind, 'instantOnAssign', event.target.checked)}
							/>
						)}

						{!aus && <p className={styles.status}>{status(kind)}</p>}
					</section>
				)
			})}

			<div className={styles.actions}>
				<Btn
					loading={saving}
					disabled={!dirty}
					attention={dirty}
					type="button"
					onClick={handleSave}
					icon="send"
					iconSize={{ w: 15, h: 15 }}
					iconColor="#fff"
					width={230}
				>
					{t('Save reminders')}
				</Btn>
			</div>
		</div>
	)
}

ReminderSettings.propTypes = {
	// Das Konto, um dessen Erinnerungen es geht.
	userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	// Rolle dieses Kontos, falls schon bekannt — sonst kommt sie vom Server.
	role: PropTypes.string,
	onDirtyChange: PropTypes.func,
	onSaved: PropTypes.func,
	className: PropTypes.string
}
